/**
 * @fileoverview PurchasePuffin Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/purchase-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 *
 * Includes the e-commerce-specific E-Commerce Operations (#4) and
 * Third-Party Integrations (#5) sections.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'purchase',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PurchasePuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-02-28',
    contactEmail: 'hello@purchasepuffin.com',
    canonicalUrl: 'https://purchasepuffin.com/terms',
    ogImage: 'https://purchasepuffin.com/og-image.jpg',
    governingLaw: 'Oregon',
    description:
      'PurchasePuffin terms of service. Covers usage, e-commerce platform responsibilities, and liability.',
  },
  sections: [
    {
      id: 'acceptance-of-terms',
      title: '1. Acceptance of Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName} ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is an e-commerce platform that gives each business its own isolated storefront, with product customization, partner portals, dynamic pricing, and integrated content management.',
        },
      ],
    },
    {
      id: 'user-responsibilities',
      title: '3. User Responsibilities',
      order: 3,
      blocks: [
        { type: 'paragraph', text: 'As a user of the Service, you agree to:' },
        {
          type: 'list',
          items: [
            'Provide accurate and complete information when using the Service',
            'Review orders and product configurations before processing',
            'Maintain the security of your account credentials',
            'Comply with all applicable federal, state, and local commerce and tax laws',
            'Not use the Service for any unlawful purpose',
          ],
        },
      ],
    },
    {
      id: 'e-commerce-operations',
      title: '4. E-Commerce Operations',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} facilitates e-commerce operations including product catalog management, order processing, and payment collection through Stripe. You acknowledge that you are responsible for the accuracy of product listings, pricing, and order fulfillment. {productName} is a platform provider and does not process payments directly.',
        },
      ],
    },
    {
      id: 'third-party-integrations',
      title: '5. Third-Party Integrations',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'The Service integrates with Stripe for payments, USPS and UPS for shipping, TaxJar for tax calculation, and SendGrid for email. Your use of these services is subject to their respective terms of service and privacy policies. {productName} is not responsible for any issues arising from third-party services.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '6. Limitation of Liability',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'To the maximum extent permitted by law, {productName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to errors in order processing, missed shipments, or data loss.',
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '7. Intellectual Property',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'All content, features, and functionality of the Service are owned by {productName} and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.',
        },
      ],
    },
    {
      id: 'termination',
      title: '8. Termination',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may terminate or suspend your access to the Service at any time, without prior notice or liability, for any reason. Upon termination, your right to use the Service will immediately cease.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '9. Changes to Terms',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these Terms of Service at any time. We will provide notice of material changes by posting the updated terms on this page. Your continued use of the Service after changes constitutes acceptance of the new terms.',
        },
      ],
    },
    {
      id: 'governing-law',
      title: '10. Governing Law',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'These Terms of Service shall be governed by and construed in accordance with the laws of the State of {governingLaw}, without regard to its conflict of law provisions.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '11. Contact Us',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
